/**
 * Robust date formatting utility for Yaseero
 */

/**
 * Safely parses a date string, preserving local time for local dates/times (no timezone offset)
 * while properly converting UTC timestamps (containing Z or timezone offset).
 */
const parseDateString = (dateStr) => {
    if (!dateStr) return new Date(NaN);
    if (dateStr instanceof Date) return dateStr;
    
    const str = String(dateStr);
    
    // Check if it has an explicit timezone (Z or +offset or -offset after T)
    const hasZ = str.includes('Z');
    const hasOffset = str.includes('+') || (str.includes('T') && str.split('T')[1].includes('-'));
    
    if (hasZ || hasOffset) {
        return new Date(str);
    }
    
    // If it's a date-only string (e.g., "2026-06-13")
    if (!str.includes('T')) {
        return new Date(`${str}T00:00:00`);
    }
    
    // If it's a date-time string without timezone (e.g., "2026-06-13T23:59:59")
    return new Date(str);
};

/**
 * Formats a date string to a localized date and time.
 */
export const formatDateTime = (dateStr, options = {}) => {
    if (!dateStr) return '—';
    
    const date = parseDateString(dateStr);
    if (isNaN(date.getTime())) return String(dateStr);

    const defaultOptions = {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        ...options
    };

    return date.toLocaleString('en-GB', defaultOptions);
};

/**
 * Returns a human-readable "time ago" string.
 */
export const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    
    const date = parseDateString(dateStr);
    if (isNaN(date.getTime())) return '';
    
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hrs > 0) return `${hrs}h ago`;
    if (mins > 0) return `${mins}m ago`;
    return 'Just now';
};

/**
 * Formats a date only (no time).
 */
export const formatDateOnly = (dateStr) => {
    if (!dateStr) return '—';
    return formatDateTime(dateStr, { hour: undefined, minute: undefined });
};

/**
 * Formats any date string strictly as dd-MM-yyyy (e.g., 05-09-2026 or 24-08-2026, 09:25 PM).
 */
export const formatWarehouseDate = (dateStr, includeTime = false) => {
    if (!dateStr) return '—';
    const str = String(dateStr).trim();
    if (!str || str === '—') return '—';

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-');
        return `${d}-${m}-${y}`;
    }

    const date = parseDateString(str);
    if (isNaN(date.getTime())) {
        const match = str.match(/(\d{4})[-/](\d{2})[-/](\d{2})/);
        if (match) {
            return `${match[3]}-${match[2]}-${match[1]}`;
        }
        return str;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    if (includeTime) {
        let hours = date.getHours();
        const mins = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const formattedHours = String(hours).padStart(2, '0');
        return `${day}-${month}-${year}, ${formattedHours}:${mins} ${ampm}`;
    }

    return `${day}-${month}-${year}`;
};
